import Skeleton from 'react-loading-skeleton'
import { getBrandAndCouponName } from 'src/utils'
import { Barcode } from 'src/components/common/Barcode'
import { Roboto_num1_b20, Roboto_num2_b16 } from 'src/styles/fonts/RobotoFonts'
import React, { useEffect } from 'react'
import styled, { css } from 'styled-components'
import { Container } from 'src/components/common/Container'
import { Typography } from 'src/components/common/Typography'
import {
  Noto_heading2_r24,
  Noto_heading3_b26,
  Noto_heading3_r20,
} from 'src/styles/fonts/NotoSansKrFonts'
import { ellip } from 'src/styles/mixin'
import {
  DownloadCouponPathCheckSpace,
  FreeCouponDetailSpace,
  GiftDetailInfoByGiftIdxSpace,
  PurchaseHistoryDetailSpace,
} from 'src/types/api'
import { DateUtils } from 'src/utils/date'
import useDynamicWidth from 'src/hooks/useDynamicWidth.hook'
import { captureMessage, SENTRY_SEVERITY } from 'src/utils/sentry'

export interface DownLoadCouponLayoutProps {
  couponDetailData:
    | PurchaseHistoryDetailSpace.Response
    | FreeCouponDetailSpace.Response
    | GiftDetailInfoByGiftIdxSpace.Response
    | DownloadCouponPathCheckSpace.Response
  componentRef?: React.RefObject<HTMLDivElement>
  barcode: string
}

const DownloadCouponLayout = ({
  couponDetailData,
  componentRef,
  barcode,
}: DownLoadCouponLayoutProps) => {
  const { DateFormat } = DateUtils
  const { deviceWidth } = useDynamicWidth()
  const textLogo = `${process.env.PUBLIC_URL}/images/text_logo_simple.svg`

  const { CouponName, CouponImage, ExpireDT, RegularPrice, BrandName, PinNumber } =
    couponDetailData as PurchaseHistoryDetailSpace.Response

  const pinNumber = PinNumber?.trim()

  useEffect(() => {
    const container = document.getElementById('download-layout-container')
    const couponImg = container?.querySelector<HTMLImageElement>('img[alt="쿠폰 이미지"]')

    if (!container) {
      captureMessage('[DownloadCoupon] DownloadLayoutContainer DOM Not Found', {
        level: SENTRY_SEVERITY.Info,
      })
      return
    }

    if (!couponImg)
      captureMessage('[DownloadCoupon] CouponImg DOM Not Found', {
        level: SENTRY_SEVERITY.Info,
      })
  }, [])

  return (
    <S.DownloadLayoutWrap>
      <S.DownloadLayoutContainer
        $option="flex_column"
        ref={componentRef}
        $deviceWidth={deviceWidth}
        id="download-layout-container"
      >
        <S.TextLogoWrap>
          <img src={textLogo} alt="휴대폰쿠폰지갑" height={24} />
        </S.TextLogoWrap>
        <img src={CouponImage} alt="쿠폰 이미지" width={300} height={300} />
        <Barcode.Image value={`${barcode}`} barcode_height={125} />
        <Barcode.Text value={`${barcode}`} fontStyle={Roboto_num2_b16} />
        <S.CouponDetailContainer $option="flex_column">
          <Container $option="flex_column" $row_gap="16px">
            <S.BrandName as={!!CouponName ? 'span' : Skeleton}>
              {BrandName || getBrandAndCouponName(CouponName).brandName}
            </S.BrandName>
            <S.CouponName as={!!CouponName ? 'span' : Skeleton}>
              {getBrandAndCouponName(CouponName).couponName.slice(0, 35)}
              {getBrandAndCouponName(CouponName).couponName.length >= 35 && '···'}
            </S.CouponName>
          </Container>
          <S.Divider />
          <Container $option="flex_column" $row_gap="10px">
            <Container $option="flex_row_space_between">
              <Typography $fontStyle={Noto_heading3_r20} $color="var(--black-50)">
                유효기간
              </Typography>
              <S.ValidDay as={!!ExpireDT ? 'span' : Skeleton}>
                {DateFormat(ExpireDT, 'YYYY.MM.DD')}
              </S.ValidDay>
            </Container>
            <Container $option="flex_row_space_between">
              <Typography $fontStyle={Noto_heading3_r20} $color="var(--black-50)">
                상품 금액
              </Typography>
              <S.RegularPrice as={!!RegularPrice ? 'span' : Skeleton}>
                {RegularPrice?.toLocaleString()}원
              </S.RegularPrice>
            </Container>
            {!!pinNumber && (
              <Container $option="flex_row_space_between">
                <Typography $fontStyle={Noto_heading3_r20} $color="var(--black-50)">
                  쿠폰인증번호
                </Typography>
                <S.ValidDay as="span">{pinNumber}</S.ValidDay>
              </Container>
            )}
          </Container>
        </S.CouponDetailContainer>
      </S.DownloadLayoutContainer>
    </S.DownloadLayoutWrap>
  )
}

export default DownloadCouponLayout

const S = {
  DownloadLayoutWrap: styled(Container)`
    position: relative;
    z-index: -1000;
    pointer-events: none;
    position: relative;
  `,
  DownloadLayoutContainer: styled(Container)<{ $deviceWidth: number }>`
    position: absolute;
    left: 0;
    top: 0;
    align-items: center;
    padding: 0 30px 30px;
    width: 500px;
    height: 965px;
    background-color: var(--white);
    transform: scale(${({ $deviceWidth }) => ($deviceWidth > 500 ? 1 : $deviceWidth / 600)});
    transform-origin: top left;
  `,
  TextLogoWrap: styled(Container)`
    display: flex;
    justify-content: center;
    padding: 30px 0;
  `,
  CouponDetailContainer: styled(Container)`
    align-items: center;
    margin-top: 20px;
    padding: 40px 30px;
    border-radius: 30px;
    background-color: var(--black-4);
  `,
  BrandName: styled(Typography)`
    ${Noto_heading2_r24};
    color: var(--black-100);
    text-align: center;
  `,
  CouponName: styled(Typography)`
    ${Noto_heading3_b26};
    color: var(--black-100);
    ${ellip('line2')};
    text-align: center;
  `,
  Divider: styled(Container)`
    margin: 40px 0;
    height: 0;
    border-top: 1px solid var(--black-20);
  `,
  ValidDay: styled(Typography)`
    ${Roboto_num1_b20};
    color: var(--black-50);
  `,
  RegularPrice: styled(Typography)`
    ${Roboto_num1_b20};
    color: var(--black-50);
  `,
  BarcodeDeem: css`
    width: 284px;
    height: 122px;
    backdrop-filter: blur(2px);
    background: rgba(255, 255, 255, 0.9);
    margin-top: -16px;
    position: absolute;
    z-index: 1;
    left: 50%;
    transform: translate(-50%, 0);
    gap: 10px;
    align-items: center;
    justify-content: center;
  `,
  BarcodeStatusIcon: styled.img`
    display: flex;
    width: 35px;
    height: 34px;
    margin: 0 auto;
  `,
}
